// The ONE operator-facing wording for an upload's ``removal_reason``, shared by the
// Drive tree and the Gmail tree so a tombstone reads identically wherever it renders
// (the "surfaces that must agree" rule). The reason tokens are globally unique, so
// one map serves both vocabularies.
//
// Tombstones used to fall back to the RAW token when a reason wasn't in the map, so
// operators saw "duplicate_content" / "superseded_by_zip_expansion" in red italics.
// ``removalReasonLabel`` closes that class for good: an unmapped token degrades to
// sentence-cased English rather than leaking the identifier (INRT-2560).
const REMOVAL_REASON_TEXT: Record<string, string> = {
  // Drive sync
  deleted_in_drive: "Deleted from Drive",
  renamed_to_excluded: "Renamed to match an exclusion pattern",
  moved_to_excluded: "Moved into an excluded folder",
  now_matches_exclude_pattern: "Name now matches an exclusion pattern",
  folder_excluded_in_config: "Folder is excluded in the sync settings",
  too_large: "Too large to sync (over 500 MB) - split or exclude the file",
  junk_file: "Office lock / temporary file (~$…) - not a real document",
  superseded_by_zip_expansion:
    "Archive expanded - each file inside it is listed separately",
  root_removed: "Its Drive folder was removed from this project's sync",
  deletion_overridden: "An agent is bringing this deleted copy back - the next sync restores it",
  // Gmail sync
  post_filter_excluded: "Excluded by a semantic exception",
  query_deleted: "Originating query was deleted",
  // Operator decisions
  operator_excluded: "Excluded by the operator",
  operator_excluded_wrong_site: "Excluded by the operator - belongs to another site",
  // Content dedup. A fold loser whose winner is still ACTIVE renders as an ordinary
  // file (the BE resolves it to a dedup alias) and a fold loser with no distinct
  // place is dropped, so these only reach the operator when the winner is itself
  // gone - but a raw token must never surface either way.
  duplicate_content: "Duplicate of another copy of this file, which was removed",
  duplicate: "Duplicate of another copy of this file, which was removed",
  no_active_sighting: "No longer present in any synced location",
};

export function removalReasonLabel(reason: string | null | undefined): string {
  if (!reason) return "";
  const known = REMOVAL_REASON_TEXT[reason];
  if (known) return known;
  // Unknown token (a reason added BE-side without updating this map): degrade to
  // "Some new reason" rather than showing the operator "some_new_reason".
  const words = reason.replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
}
